"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Facebook, Linkedin, Youtube, Instagram, X } from "lucide-react";
import { trackPhoneClick, trackWhatsAppClick } from "@/utils/gtm";
import { openWhatsApp, whatsappMessages } from "@/utils/whatsapp";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import { FORM_CONTEXTS } from "@/utils/innovlistWidget";

export function Footer() {
  const pathname = usePathname();
  const router = useRouter();
  const currentPage = pathname || "/";

  const socialLinks = [
    { icon: Linkedin, href: "https://www.linkedin.com/company/seasoft-classes", label: "LinkedIn" },
    { icon: Facebook, href: "https://www.facebook.com/SeasoftClasses/", label: "Facebook" },
    // { icon: Instagram, href: "https://www.instagram.com/", label: "Instagram" },
    { icon: Youtube, href: "https://www.youtube.com/@ITTRAININGINUSA", label: "YouTube" },
    { icon: X, href: "https://x.com/Seasoft_Classes", label: "X" },
  ];

  const quickLinks = [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about-us" },
    { label: "Courses", href: "/courses" },
    { label: "Services", href: "/services" },
    { label: "Online Training", href: "/online-training" },
    { label: "Blog", href: "/blog" },
    { label: "Testimonials", href: "/testimonials" },
    { label: "Contact", href: "/contact-us" },
  ];

  const popularCourses = [
    { label: "Power BI", href: "/courses/power-bi" },
    { label: "Salesforce Administrator", href: "/courses/salesforce-administrator" },
    { label: "Business Analysis", href: "/courses/business-analysis" },
    { label: "SAS — Statistical Analysis", href: "/courses/sas-statistical-analysis" },
    { label: "MicroStrategy", href: "/courses/microstrategy" },
    { label: "Java", href: "/courses/java" },
    { label: "QA Testing", href: "/courses/qa-testing" },
    { label: "PMP", href: "/courses/pmp" },
    { label: "Selenium", href: "/courses/selenium" },
    { label: "Linux Administration", href: "/courses/linux-administration" },
    { label: "CCNA", href: "/courses/ccna" },
    { label: "NetSuite Functional", href: "/courses/netsuite-functional" },
  ];

  return (
    <footer className="bg-[#002147] text-white">
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-7 text-center">
          <p className="text-blue-100 text-[18px] font-medium">
            Empowering IT Careers Worldwide - One Learner at a Time.
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="space-y-8">
              <Link href="/" className="inline-flex" aria-label="Seasoft Classes home">
                <span className="rounded-xl bg-white px-3 py-2 shadow-sm">
                  <Image
                    src="/images/seasoft-logo.svg"
                    alt="Seasoft Classes"
                    width={210}
                    height={62}
                    className="h-10 w-auto"
                  />
                </span>
              </Link>

              <div className="hidden text-lg font-bold mb-2">About</div>
              <p className="text-[15px] text-gray-300 leading-relaxed">
                Seasoft Classes is a premier online IT training provider serving learners across the USA, Canada, UK,
                and Asia. We offer 300+ live, instructor-led courses in data analytics, ERP, networking,
                cybersecurity, programming, and project management.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row sm:gap-4">
                <EnrollButton
                  label="Enroll Now"
                  variant="primary"
                  // size="sm"
                  context={FORM_CONTEXTS.FOOTER_CTA}
                  icon={false}
                />
                {/* <button
                  onClick={() => router.push("/payment-form")}
                  className="inline-flex items-center justify-center bg-white text-blue-600 hover:bg-gray-100 rounded-lg font-semibold shadow-md transition-all duration-200 px-6 py-3"
                  type="button"
                  aria-label="Buy Now"
                >
                  Buy Now
                </button> */}
              </div>
            </div>

            <div>
              <div className="text-lg font-bold mb-8">Quick Links</div>
              <ul className="space-y-4 text-[15px] text-gray-400">
                {quickLinks.map((item) => (
                  <li key={item.label}>
                    <Link href={item.href} className="hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-lg font-bold mb-8">Popular Courses</div>
              <ul className="space-y-4 text-[15px] text-gray-400">
                {popularCourses.map((item) => (
                  <li key={item.label}>
                    <Link href={item.href} className="hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-8">
              <div>
                <div className="text-lg font-bold mb-6">Contact</div>
                <ul className="space-y-3 text-[15px] text-gray-300">
                  <li>
                    <a
                      href="tel:+17087669765"
                      onClick={() => trackPhoneClick("+17087669765", currentPage)}
                      className="hover:text-white transition-colors"
                    >
                      +1 (708) 766-9765
                    </a>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        openWhatsApp(whatsappMessages.default);
                        trackWhatsAppClick("footer_contact", "default");
                      }}
                      className="flex items-center gap-2 text-gray-300 hover:text-[#25D366] transition-colors group"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px] text-gray-400 group-hover:text-[#25D366]">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                      <span>WhatsApp: +1 (209) 210-3187</span>
                    </button>
                  </li>
                  <li>training@seasoftclasses.com</li>
                  <li>15 Huntington Circle, Naperville, IL 60540</li>
                </ul>
              </div>

              <div>
                <div className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3">Social Links</div>
                <div className="flex gap-2">
                  {socialLinks.map((item) => (
                    <Link
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      // Optimization 1: Security boost for external tabs
                      rel="noopener noreferrer" 
                      // Optimization 2: Passes the label dynamically to clear the Lighthouse error
                      aria-label={`Visit our ${item.label} page`} 
                      className="w-9 h-9 bg-blue-900/40 rounded flex items-center justify-center hover:bg-blue-600 transition-colors"
                    >
                      {/* Optimization 3: Hides the inner SVG paths from confused screen readers */}
                      <item.icon size={16} aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 py-8">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-semibold text-gray-400">
          <p className="text-[16px]">© 2026 Seasoft Classes. All rights reserved.</p>
          <div className="flex flex-wrap text-[16px] justify-center gap-x-6 gap-y-2">
            <Link href="/privacy-policy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link href="/terms-and-conditions" className="hover:text-white transition-colors">
              Terms & Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
